import React from 'react';
import {
  FlexiblePageTreeNode,
  SectionTreeNode,
  ComponentTreeNode,
  isSlideDeckRenderMode,
  isSlideHidden,
} from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/flexiblePagesTreeNode';
import { RenderStage } from '@mutiny-pkg/champaign-schemas/src/schemas/micrositePages/render';
import { Section } from './Section';
import { Page } from './Page';
import { SlideDeckHarness } from './SlideDeckHarness';
import { SlideFrame } from './SlideFrame';
import { renderReadonlyComponentNode, type ReadonlyRenderContext } from './readonlyNodeRenderer';

interface ReadonlyComponentRendererProps {
  componentTree: FlexiblePageTreeNode;
  overrideVariables?: Record<string, string>;
  staticAssetsBaseURL?: string | null;
  renderStage?: RenderStage;
  children?: React.ReactNode;
  sectionOverlay?: React.ReactNode;
  skipFontLoading?: boolean;
  /** When true, transforms viewport breakpoint classes (lg:) to container query classes (@lg:) for container-query-based layout. Requires a @container ancestor. */
  isEditor?: boolean;
  /** When true, slide-deck pages render slides as stacked fixed-size sections instead of the interactive harness, so the PDF/PPTX export pipeline can screenshot each one. */
  isPdfExport?: boolean;
  /** When true, slide-deck pages render only the first slide scaled to fill the frame (no presentation player/chrome), so small thumbnail iframes show a legible cover instead of a letterboxed slide. */
  isThumbnail?: boolean;
}

export const ReadonlyComponentRenderer = React.memo(
  ({
    componentTree,
    overrideVariables,
    staticAssetsBaseURL,
    renderStage,
    children,
    sectionOverlay,
    skipFontLoading = true,
    isEditor = false,
    isPdfExport = false,
    isThumbnail = false,
  }: ReadonlyComponentRendererProps) => {
    const emptyMentionItems = React.useMemo(() => [], []);
    const emptyRefetch = React.useCallback(async () => {}, []);

    // Only pass staticAssetsBaseURL in Preview renderStage, not in Publish
    const resolvedstaticAssetsBaseURL = renderStage === RenderStage.Preview ? staticAssetsBaseURL : undefined;

    const renderCtx: ReadonlyRenderContext = {
      overrideVariables,
      staticAssetsBaseURL: resolvedstaticAssetsBaseURL,
      isEditor,
      mentionItems: emptyMentionItems,
      refetchMentionItems: emptyRefetch,
    };

    const renderComponentNode = (treeNode: ComponentTreeNode): React.ReactNode =>
      renderReadonlyComponentNode(treeNode, renderCtx);

    const renderPageNode = (pageTreeNode: FlexiblePageTreeNode): React.ReactNode => {
      const isSlideDeck = isSlideDeckRenderMode(pageTreeNode.data.renderMode);
      const renderSlidesInline = isSlideDeck && isPdfExport;

      const renderSectionNode = (sectionNode: SectionTreeNode, forceRenderAsSlide = false): React.ReactNode => {
        return (
          <Section
            id={sectionNode.id}
            key={sectionNode.id}
            {...sectionNode.data}
            staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
            isEditor={isEditor}
            renderAsSlide={renderSlidesInline || forceRenderAsSlide}
            sectionOverlay={sectionOverlay}
          >
            {sectionNode.children?.map((childNode: ComponentTreeNode) => renderComponentNode(childNode))}
          </Section>
        );
      };

      // Hidden slides stay in the editor but never reach a viewer: dropping them here covers the
      // published deck, the preview, the thumbnail, and the PDF/PPTX exports (both select sections
      // from this rendered DOM), and renumbers the harness for free since it counts its children.
      const sections = isSlideDeck
        ? (pageTreeNode.children ?? []).filter((childNode) => !isSlideHidden(childNode))
        : pageTreeNode.children;

      const sectionChildren = sections?.map((childNode) => renderSectionNode(childNode));
      // The thumbnail shows only slide one. `renderAsSlide` marks the section a `data-slide-active`
      // `group/slide` so the deck's entrance animations resolve to their final (visible) state —
      // without it the slide renders fully transparent.
      const firstSectionNode = sections?.[0];
      const firstSlide = firstSectionNode ? renderSectionNode(firstSectionNode, true) : null;

      return (
        <Page
          treeNode={pageTreeNode}
          skipFontLoading={skipFontLoading}
          staticAssetsBaseURL={resolvedstaticAssetsBaseURL}
        >
          {children}
          {/* Freeze CSS transitions/animations and hide scrollbar gutters so neither bleeds into the
              export screenshots — a static capture needs a deterministic frame. (Custom-component
              entrance animations are JS/SSR, handled separately at the SSR boundary, not by this CSS.) */}
          {isPdfExport && (
            <style>{`
              [data-flexible-page-root] *,
              [data-flexible-page-root] *::before,
              [data-flexible-page-root] *::after {
                transition: none !important;
                animation: none !important;
              }
              html, body, *, *::before, *::after {
                scrollbar-width: none !important;
                -ms-overflow-style: none !important;
              }
              html::-webkit-scrollbar,
              body::-webkit-scrollbar,
              *::-webkit-scrollbar {
                display: none !important;
                width: 0 !important;
                height: 0 !important;
              }
            `}</style>
          )}
          {isSlideDeck && isThumbnail ? (
            firstSlide ? (
              <SlideFrame index={0} staticScale>
                {firstSlide}
              </SlideFrame>
            ) : null
          ) : isSlideDeck && !renderSlidesInline ? (
            <SlideDeckHarness isEditor={isEditor}>{sectionChildren}</SlideDeckHarness>
          ) : (
            sectionChildren
          )}
        </Page>
      );
    };

    return <>{renderPageNode(componentTree)}</>;
  },
  (prevProps, nextProps) => {
    return (
      prevProps.componentTree === nextProps.componentTree &&
      prevProps.overrideVariables === nextProps.overrideVariables &&
      prevProps.staticAssetsBaseURL === nextProps.staticAssetsBaseURL &&
      prevProps.renderStage === nextProps.renderStage &&
      prevProps.children === nextProps.children &&
      prevProps.sectionOverlay === nextProps.sectionOverlay &&
      prevProps.skipFontLoading === nextProps.skipFontLoading &&
      prevProps.isEditor === nextProps.isEditor &&
      prevProps.isPdfExport === nextProps.isPdfExport &&
      prevProps.isThumbnail === nextProps.isThumbnail
    );
  },
);

ReadonlyComponentRenderer.displayName = 'ReadonlyComponentRenderer';
